import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, date, object, string, number, mixed } from 'yup';

export type CreateFreebetsFormState = {
  providerId: string;
  campaignId: string;
  file: File | null;
  validityDate: Date | string | null;
  count: number;
  minMultiplier: number;
  betNominal: number;
  currency: string;
};

type CreateFreebetsValidationLimits = {
  minAutoCashout?: number | null;
  maxAutoCashout?: number | null;
  minBet?: number | null;
  maxBet?: number | null;
};

const getTomorrowStart = (): Date => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
};

const xlsxFileValidation = mixed<File>()
  .required('player ids file is required field')
  .test('xlsx-only', 'only xlsx and xls files are allowed', (value) => {
    if (!value) {
      return true;
    }

    const fileName = value?.name?.toLowerCase() ?? '';
    return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
  });

export const createFreebetsFormStateDefault: CreateFreebetsFormState = {
  providerId: '',
  campaignId: '',
  file: null,
  validityDate: getTomorrowStart().toISOString(),
  count: 0,
  minMultiplier: 0,
  betNominal: 0,
  currency: '',
};

export const createFreebetsSchema = (
  limits?: CreateFreebetsValidationLimits
): ObjectSchema<Partial<CreateFreebetsFormState>> => {
  let minMultiplierValidation = number()
    .required('min multiplier is required field')
    .positive('min multiplier must be a positive number');
  let betNominalValidation = number()
    .required('bet nominal is required field')
    .positive('bet nominal must be a positive number');

  if (typeof limits?.minAutoCashout === 'number') {
    minMultiplierValidation = minMultiplierValidation.moreThan(
      limits.minAutoCashout,
      `min multiplier must be greater than ${limits.minAutoCashout}`
    );
  }

  if (typeof limits?.maxAutoCashout === 'number') {
    minMultiplierValidation = minMultiplierValidation.lessThan(
      limits.maxAutoCashout,
      `min multiplier must be less than ${limits.maxAutoCashout}`
    );
  }

  if (typeof limits?.minBet === 'number') {
    betNominalValidation = betNominalValidation.moreThan(
      limits.minBet,
      `bet nominal must be greater than ${limits.minBet}`
    );
  }

  if (typeof limits?.maxBet === 'number') {
    betNominalValidation = betNominalValidation.lessThan(
      limits.maxBet,
      `bet nominal must be less than ${limits.maxBet}`
    );
  }

  return object<Partial<CreateFreebetsFormState>>().shape({
    providerId: string().required('provider id is required field'),
    campaignId: string().required('campaign id is required field'),
    file: xlsxFileValidation,
    validityDate: date()
      .min(getTomorrowStart(), 'validity date should not be earlier than tomorrow')
      .required('validity date is required field'),
    count: number().required('count is required field').positive('count must be a positive number'),
    minMultiplier: minMultiplierValidation,
    betNominal: betNominalValidation,
    currency: string().required('currency is required field'),
  });
};

export const createFreebetsSchemaDefault: ObjectSchema<Partial<CreateFreebetsFormState>> =
  createFreebetsSchema();

type CreateFreebetsFormContextType = {
  schema: ObjectSchema<Partial<CreateFreebetsFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateFreebetsFormState>>>;
  methods?: UseFormReturn<CreateFreebetsFormState>;
};

const createFreebetsFormContextDefault = {
  schema: createFreebetsSchemaDefault,
  setSchema: noop,
} satisfies CreateFreebetsFormContextType;

export const CreateFreebetsFormContext = createContext<CreateFreebetsFormContextType>(
  createFreebetsFormContextDefault
);

export const useCreateFreebetsForm = () => {
  const { methods } = useContext(CreateFreebetsFormContext);
  if (!methods) {
    throw new Error('CreateFreebetsFormContext is empty');
  }

  return methods;
};

export const useManageCreateFreebetsSchema = () => {
  const { setSchema } = useContext(CreateFreebetsFormContext);

  return { setSchema };
};
